import {
  SELECTABLE_ORDER_SUBCATEGORIES,
  ORDER_SUBCATEGORIES,
  SUPPORT_CATEGORIES,
  BUILD_SUBCATEGORIES,
  ADDRESS_SOURCES,
  ADDRESS_ACTIONS,
} from '@uniticket/shared';
import type { TFunction } from 'i18next';
import type { TicketListItem } from '@uniticket/shared';

/**
 * Options and labels for the ticket list's Category filter.
 *
 * This is a builder, not a constant table like `filter-options.ts`, for two reasons: the labels
 * must be translated, and the source lists use three DIFFERENT pre-existing i18n key conventions
 * that have to be honoured rather than unified:
 *
 *   orderSubcategory / buildSubcategory -> UPPER_SNAKE keys, identical to the value
 *   supportCategory                     -> lower_snake keys (hence .toLowerCase())
 *   addressSource / addressAction       -> the English value itself, spaces included
 */

/**
 * i18next's own `t`, deliberately un-narrowed. A hand-written `(key: string, fallback?: string)`
 * alias — which is how every call site here actually uses it — is NOT assignable from the
 * TFunction that `useTranslation` returns: TFunction carries a stack of overloads whose second
 * parameter is an options object, so the narrow alias fails with TS2345 at every call site.
 */
type Translate = TFunction<any, any>;

export interface CategoryOption {
  value: string;
  label: string;
}

/**
 * Options for the Category pill, for the ONE ticket type currently pinned.
 *
 * Returns [] for 'ALL', for ADDRESS_MANAGEMENT (which gets its own Source/Action pills) and for
 * anything unknown. An empty list is what renders the pill DISABLED — it is never hidden.
 */
export function categoryOptionsForType(type: string, t: Translate): CategoryOption[] {
  switch (type) {
    case 'OPEN_ORDER':
      // SELECTABLE_*, not ORDER_SUBCATEGORIES: MOVE_FTU is retired and must not be offered.
      // The same constant drives the creation wizard's tiles, so the two cannot drift apart.
      return SELECTABLE_ORDER_SUBCATEGORIES.map((v: string) => ({
        value: v,
        label: t(`common:orderSubcategory.${v}`, v),
      }));
    case 'SUPPORT_REQUEST':
      return SUPPORT_CATEGORIES.map((v: string) => ({
        value: v,
        label: t(`common:supportCategory.${v.toLowerCase()}`, v),
      }));
    case 'BUILD':
      return BUILD_SUBCATEGORIES.map((v: string) => ({
        value: v,
        label: t(`common:buildSubcategory.${v}`, v),
      }));
    default:
      return [];
  }
}

export function addressSourceOptions(t: Translate): CategoryOption[] {
  return ADDRESS_SOURCES.map((v: string) => ({ value: v, label: t(`common:addressSource.${v}`, v) }));
}

export function addressActionOptions(t: Translate): CategoryOption[] {
  return ADDRESS_ACTIONS.map((v: string) => ({ value: v, label: t(`common:addressAction.${v}`, v) }));
}

/**
 * Every value VALID for a type, retired ones included — the prune effect's yardstick, and
 * deliberately WIDER than categoryOptionsForType(). The pill must not offer MOVE_FTU, but a URL
 * or a saved filter naming it is still meaningful and must not be silently rewritten; only
 * values belonging to a different ticket type are dropped.
 */
export function categoryValuesForType(type: string): readonly string[] {
  switch (type) {
    case 'OPEN_ORDER':
      return ORDER_SUBCATEGORIES;
    case 'SUPPORT_REQUEST':
      return SUPPORT_CATEGORIES;
    case 'BUILD':
      return BUILD_SUBCATEGORIES;
    default:
      return [];
  }
}

/**
 * Label for one selected value, for the active-filter chips.
 *
 * Translates by the SAME key convention as the options builder rather than looking the value up
 * in the options list — so a retired value such as MOVE_FTU still renders "Move FTU" on its chip
 * instead of a raw UPPER_SNAKE string.
 */
export function categoryLabel(value: string, type: string, t: Translate): string {
  switch (type) {
    case 'SUPPORT_REQUEST':
      return t(`common:supportCategory.${value.toLowerCase()}`, value);
    case 'BUILD':
      return t(`common:buildSubcategory.${value}`, value);
    case 'OPEN_ORDER':
      return t(`common:orderSubcategory.${value}`, value);
    default:
      // No type pinned: the value can only have come from a URL, and we cannot know which column
      // it belongs to. Order subcategories are by far the likeliest, and t() falls back to the
      // raw value when the key is missing, so nothing is ever rendered as a bare key.
      return t(`common:orderSubcategory.${value}`, value);
  }
}

/**
 * The row's category, whatever its ticket type — one derivation shared by the list column and
 * the CSV export so they cannot drift. Address Management reads "Source / Action", the same
 * composition `reporting.service.ts`'s CATEGORY_EXPR uses. Returns null when unset.
 */
export function ticketCategoryRaw(ticket: TicketListItem): string | null {
  switch (ticket.ticketType) {
    case 'ADDRESS_MANAGEMENT': {
      const parts = [ticket.addrSource, ticket.addrAction].filter((p): p is string => !!p);
      return parts.length > 0 ? parts.join(' / ') : null;
    }
    case 'SUPPORT_REQUEST':
      return ticket.supportCategory ?? null;
    case 'BUILD':
      return ticket.buildSubcategory ?? null;
    default:
      return ticket.orderSubcategory ?? null;
  }
}

/** The same derivation, translated, for on-screen display. Em-dash when the row has no category. */
export function ticketCategoryLabel(ticket: TicketListItem, t: Translate): string {
  if (ticket.ticketType === 'ADDRESS_MANAGEMENT') {
    const parts = [
      ticket.addrSource ? t(`common:addressSource.${ticket.addrSource}`, ticket.addrSource) : null,
      ticket.addrAction ? t(`common:addressAction.${ticket.addrAction}`, ticket.addrAction) : null,
    ].filter((p): p is string => !!p);
    return parts.length > 0 ? parts.join(' / ') : '—';
  }
  const raw = ticketCategoryRaw(ticket);
  return raw ? categoryLabel(raw, ticket.ticketType, t) : '—';
}
